import type * as React from "react"

import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { KleriLogo } from "@/components/ui/kleri-logo"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarTrigger,
} from "@/components/ui/sidebar"
import { useSidebar } from "@/components/ui/use-sidebar"
import { LargeLogo } from "@/customer/large-logo"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

import { NavUser } from "./nav-user"

type AppSidebarProps = React.ComponentProps<typeof Sidebar> & {
  children?: React.ReactNode
  /**
   * Optional click handler for the large logo.
   * Module-specific sidebars should pass their own start-new-chat handler here.
   */
  onLogoClick?: () => void
}

export function AppSidebar({ children, onLogoClick, ...props }: AppSidebarProps) {
  const { state, isMobile } = useSidebar()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const logoClickHandler = onLogoClick ?? (() => navigate("/"))

  const isCollapsed = state === "collapsed" && !isMobile

  return (
    <Sidebar
      resizable
      className="h-svh group-data-[side=left]:border-r-0"
      collapsible="icon"
      data-testid="app-sidebar"
      {...props}
    >
      <SidebarHeader
        // Clearspace: half the K height on all sides (DESIGN.md "Logo") —
        // the mark is 32px tall, so 16px padding around it.
        className={cn("flex h-16 flex-row items-center px-4", {
          "justify-center": isCollapsed,
          "justify-between": !isCollapsed,
        })}
      >
        <LargeLogo
          data-testid="sidebar-logo"
          className={cn("h-8 w-auto max-w-[170px] cursor-pointer", {
            hidden: isCollapsed,
          })}
          onClick={logoClickHandler}
        />
        <SidebarTrigger className="hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hidden size-10 shrink-0 items-center justify-center rounded-md transition-[background-color,color,scale] duration-200 active:scale-[0.99] lg:flex" />
      </SidebarHeader>

      <SidebarContent className="gap-1">{children}</SidebarContent>

      <SidebarFooter className="mt-auto">
        <NavUser />
        <section className="flex items-center justify-between">
          <section className="flex flex-wrap items-center gap-1 py-1">
            <p
              className={cn("text-muted-foreground text-[10px]", {
                hidden: isCollapsed,
              })}
            >
              Powered by
            </p>
            <KleriLogo
              data-testid="sidebar-logo"
              title="Kleri"
              className={cn("h-3 w-auto", {
                hidden: isCollapsed,
              })}
            />
          </section>
          <p
            data-testid="app-version"
            className={cn("text-muted-foreground text-[10px]", {
              hidden: isCollapsed,
            })}
          >
            {t("settingsmenu.version")}
            {SettingsService.getInstance().settings.applicationVersion}
          </p>
        </section>
      </SidebarFooter>
    </Sidebar>
  )
}
